import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';
import { format } from 'date-fns';

/**
 * Service for comprehensive analytics including conversion funnels, feature adoption, and retention cohorts
 */

export const analyticsService = {
  /**
   * Get conversion funnel analytics
   */
  async getConversionFunnelAnalytics({ dateRange, segment, cohort }) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      // Generate mock data for comprehensive conversion funnel analytics
      const mockData = {
        overallConversionRate: 12.5,
        conversionTrend: 2.3,
        trialConversionRate: 35.8,
        trialTrend: 5.1,
        arpu: 24.99,
        arpuTrend: 8.2,
        featureGateConversion: 18.7,
        featureGateTrend: -1.5,
        
        funnelStages: [
          { stage: 'Registration', description: 'New user signup', users: 10000 },
          { stage: 'First Check-in', description: 'Completed first daily check-in', users: 7500 },
          { stage: 'Feature Gate Encounter', description: 'Viewed Premium feature prompt', users: 4200 },
          { stage: 'Trial Started', description: 'Activated Premium trial', users: 2100 },
          { stage: 'Premium Activated', description: 'Converted to paid Premium', users: 1250 }
        ],
        
        featureAdoption: [
          { feature: 'Advanced Analytics', adoptionRate: 45.2, premiumAdoption: 78.5 },
          { feature: 'Symptom Tracking', adoptionRate: 89.3, premiumAdoption: 92.1 },
          { feature: 'Doctor Export', adoptionRate: 34.7, premiumAdoption: 68.9 },
          { feature: 'Caregiver Tools', adoptionRate: 28.5, premiumAdoption: 85.3 },
          { feature: 'Encryption', adoptionRate: 52.1, premiumAdoption: 71.2 },
          { feature: 'Priority Support', adoptionRate: 18.9, premiumAdoption: 95.7 }
        ],
        
        segmentAnalysis: [
          {
            segment: 'New Users (0-7 days)',
            description: 'Users in first week',
            totalUsers: 2500,
            conversions: 125,
            conversionRate: 5.0,
            avgTimeToConvert: 4,
            avgRevenue: 19.99,
            upgradeLikelihood: 35
          },
          {
            segment: 'Active Users (8-30 days)',
            description: 'Regular engagement',
            totalUsers: 3200,
            conversions: 512,
            conversionRate: 16.0,
            avgTimeToConvert: 12,
            avgRevenue: 24.99,
            upgradeLikelihood: 68
          },
          {
            segment: 'Power Users (30+ days)',
            description: 'High engagement users',
            totalUsers: 1800,
            conversions: 450,
            conversionRate: 25.0,
            avgTimeToConvert: 35,
            avgRevenue: 29.99,
            upgradeLikelihood: 82
          },
          {
            segment: 'Trial Users',
            description: 'Currently in trial period',
            totalUsers: 850,
            conversions: 305,
            conversionRate: 35.9,
            avgTimeToConvert: 5,
            avgRevenue: 24.99,
            upgradeLikelihood: 75
          },
          {
            segment: 'Feature Gate Encounters',
            description: 'Viewed Premium prompts',
            totalUsers: 4200,
            conversions: 785,
            conversionRate: 18.7,
            avgTimeToConvert: 8,
            avgRevenue: 22.99,
            upgradeLikelihood: 58
          },
          {
            segment: 'Caregiver Users',
            description: 'Managing dependents',
            totalUsers: 950,
            conversions: 285,
            conversionRate: 30.0,
            avgTimeToConvert: 14,
            avgRevenue: 29.99,
            upgradeLikelihood: 85
          }
        ],
        
        cohortRetention: [
          { cohort: 'January 2026', totalUsers: 2500, day1: 85, day7: 68, day14: 52, day30: 38, premiumConversion: 12.5 },
          { cohort: 'December 2025', totalUsers: 2200, day1: 82, day7: 65, day14: 48, day30: 35, premiumConversion: 11.8 },
          { cohort: 'November 2025', totalUsers: 1950, day1: 80, day7: 62, day14: 45, day30: 32, premiumConversion: 10.5 },
          { cohort: 'October 2025', totalUsers: 1800, day1: 78, day7: 58, day14: 42, day30: 28, premiumConversion: 9.2 },
          { cohort: 'September 2025', totalUsers: 1650, day1: 75, day7: 55, day14: 38, day30: 25, premiumConversion: 8.5 }
        ]
      };

      logger?.debug('Conversion funnel analytics generated', { requestId });
      return { data: mockData, error: null };
    } catch (error) {
      logger?.error('Conversion analytics error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch conversion analytics' } };
    }
  },

  /**
   * Export conversion analytics report
   */
  async exportConversionReport({ dateRange, segment, cohort }) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: analytics } = await this.getConversionFunnelAnalytics({ dateRange, segment, cohort });
      
      if (!analytics) {
        return { data: null, error: { message: 'No data to export' } };
      }

      // Generate CSV content
      let csv = 'Premium Conversion Funnel Analytics Report\n\n';
      csv += `Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}\n`;
      csv += `Date Range: ${dateRange?.start} to ${dateRange?.end}\n\n`;

      // Key Metrics
      csv += 'KEY METRICS\n';
      csv += 'Metric,Value,Trend\n';
      csv += `Overall Conversion Rate,${analytics?.overallConversionRate}%,${analytics?.conversionTrend}%\n`;
      csv += `Trial-to-Paid Conversion,${analytics?.trialConversionRate}%,${analytics?.trialTrend}%\n`;
      csv += `Average Revenue Per User,$${analytics?.arpu},${analytics?.arpuTrend}%\n`;
      csv += `Feature Gate Conversion,${analytics?.featureGateConversion}%,${analytics?.featureGateTrend}%\n\n`;

      // Funnel Stages
      csv += 'CONVERSION FUNNEL STAGES\n';
      csv += 'Stage,Description,Users,Conversion Rate\n';
      analytics?.funnelStages?.forEach((stage, index) => {
        const conversionRate = index > 0 
          ? ((stage?.users / analytics?.funnelStages?.[0]?.users) * 100)?.toFixed(1)
          : 100;
        csv += `${stage?.stage},${stage?.description},${stage?.users},${conversionRate}%\n`;
      });
      csv += '\n';

      // Feature Adoption
      csv += 'FEATURE ADOPTION RATES\n';
      csv += 'Feature,Overall Adoption,Premium User Adoption\n';
      analytics?.featureAdoption?.forEach((feature) => {
        csv += `${feature?.feature},${feature?.adoptionRate}%,${feature?.premiumAdoption}%\n`;
      });
      csv += '\n';

      // Segmentation Analysis
      csv += 'SEGMENTATION ANALYSIS\n';
      csv += 'Segment,Total Users,Conversions,Conversion Rate,Avg Time to Convert,Avg Revenue,Upgrade Likelihood\n';
      analytics?.segmentAnalysis?.forEach((segment) => {
        csv += `${segment?.segment},${segment?.totalUsers},${segment?.conversions},${segment?.conversionRate}%,${segment?.avgTimeToConvert} days,$${segment?.avgRevenue},${segment?.upgradeLikelihood}%\n`;
      });
      csv += '\n';

      // Cohort Retention
      csv += 'COHORT RETENTION ANALYSIS\n';
      csv += 'Cohort,Total Users,Day 1,Day 7,Day 14,Day 30,Premium Conversion\n';
      analytics?.cohortRetention?.forEach((cohort) => {
        csv += `${cohort?.cohort},${cohort?.totalUsers},${cohort?.day1}%,${cohort?.day7}%,${cohort?.day14}%,${cohort?.day30}%,${cohort?.premiumConversion}%\n`;
      });

      logger?.debug('Conversion report exported', { requestId });
      return { data: csv, error: null };
    } catch (error) {
      logger?.error('Export conversion report error', error, { requestId });
      return { data: null, error: { message: 'Failed to export report' } };
    }
  },

  /**
   * Track feature adoption event
   */
  async trackFeatureAdoption(featureName) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const today = new Date()?.toISOString()?.split('T')?.[0];

      // Track feature adoption in engagement metrics
      await supabase
        ?.from('user_engagement_metrics')
        ?.upsert({
          user_id: user?.id,
          metric_date: today,
          feature_adoptions: supabase?.raw(`COALESCE(feature_adoptions, '{}')::jsonb || '{"${featureName}": true}'::jsonb`)
        }, {
          onConflict: 'user_id,metric_date'
        });

      logger?.debug('Feature adoption tracked', { featureName, userId: user?.id });
    } catch (error) {
      logger?.error('Feature adoption tracking error', error, { requestId });
    }
  },

  /**
   * Track conversion funnel stage
   */
  async trackFunnelStage(stage) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const today = new Date()?.toISOString()?.split('T')?.[0];

      // Track funnel stage progression
      await supabase
        ?.from('user_engagement_metrics')
        ?.upsert({
          user_id: user?.id,
          metric_date: today,
          funnel_stage: stage,
          funnel_stage_timestamp: new Date()?.toISOString()
        }, {
          onConflict: 'user_id,metric_date'
        });

      logger?.debug('Funnel stage tracked', { stage, userId: user?.id });
    } catch (error) {
      logger?.error('Funnel stage tracking error', error, { requestId });
    }
  }
};

export default analyticsService;